import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react';
import { Link } from 'react-router-dom';
import type { OpportunityDef, Patient } from '@/lib/types';
import { formatLastEvaluation } from '@/lib/signals';

interface OpportunityPatientsModalProps {
  def: OpportunityDef | null;
  patients: Patient[];
  onClose: () => void;
}

export function OpportunityPatientsModal({
  def,
  patients,
  onClose,
}: OpportunityPatientsModalProps) {
  return (
    <Dialog
      open={def !== null}
      onClose={onClose}
      className="ai-drawer-fade-slow fixed inset-0 z-[100] bg-black/30 dark:bg-black/55 grid place-items-center p-4 max-[760px]:p-2"
    >
      <DialogPanel className="ai-drawer-slide-slow w-full max-w-[460px] max-h-[min(640px,90vh)] bg-surface border border-line rounded-2xl flex flex-col overflow-hidden shadow-elevated">
        {def && (
          <>
            <header className="flex items-start gap-3 px-6 pt-5 pb-4 border-b border-line shrink-0">
              <div className="flex-1 min-w-0">
                <p className="text-[10.5px] uppercase tracking-[0.06em] font-semibold text-muted m-0 mb-1">
                  Pacientes en oportunidad
                </p>
                <DialogTitle as="h2" className="text-[16px] font-semibold tracking-[-0.01em] m-0 text-primary leading-[1.3] truncate">
                  {def.title}
                </DialogTitle>
                <p className="text-[12px] text-tertiary m-0 mt-0.5">
                  {patients.length} paciente{patients.length === 1 ? '' : 's'}
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                aria-label="Cerrar"
                className="text-[13px] w-7 h-7 border border-line bg-transparent text-tertiary rounded-sm cursor-pointer transition-colors hover:bg-soft hover:text-primary shrink-0"
              >
                ✕
              </button>
            </header>

            <ul className="list-none m-0 p-0 flex-1 overflow-y-auto overflow-x-hidden min-h-0">
              {patients.length === 0 && (
                <li className="px-6 py-10 text-center text-[13px] text-muted">
                  Sin pacientes en este rango.
                </li>
              )}
              {patients.map((p) => (
                <li
                  key={p.id}
                  className="border-b border-line last:border-b-0"
                >
                  <Link
                    to={`/patients/${p.id}`}
                    onClick={onClose}
                    className="flex items-center justify-between gap-3 px-6 py-3 transition-colors hover:bg-hover"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="text-[13px] font-semibold tracking-[-0.005em] truncate">
                        {p.name}
                      </div>
                      <div className="text-[11px] text-tertiary truncate">
                        {p.age} años · {p.gender} · {p.city} · última eval.{' '}
                        {formatLastEvaluation(p.lastEvaluationAt).toLowerCase()}
                      </div>
                    </div>
                    <span className="text-[11px] text-faint shrink-0" aria-hidden="true">→</span>
                  </Link>
                </li>
              ))}
            </ul>

            <footer className="px-6 py-3 border-t border-line bg-surface shrink-0">
              <p className="text-[11px] text-muted m-0">
                Click en un paciente para abrir su perfil.
              </p>
            </footer>
          </>
        )}
      </DialogPanel>
    </Dialog>
  );
}
